/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      return saved && token ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!(localStorage.getItem('user') && localStorage.getItem('token'));
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ShubbhYatra Phase 1: the login response doesn't carry `features` (new
  // flags live on /api/users/me only). AuthWrapper gates the
  // ShubbhYatraOnlyBlocker on user.features.shubbhYatraOnly, so we must
  // populate that field after login + on app boot.
  //
  // Additive-only: this never changes auth logic, never touches token
  // handling. It just folds the /me response into the cached user object.
  const refreshMe = useCallback(async () => {
    try {
      const resp = await api.get('/users/me');
      const me = resp?.data?.data;
      if (!me) return null;
      // Merge ONLY features — every other field on the cached user stays
      // exactly as the login response set it. (The login response carries
      // fields the /me response doesn't, e.g. token, and shouldn't be
      // overwritten by the /me shape.)
      setUser((prev) => {
        const merged = { ...(prev || {}), features: me.features || {} };
        try { localStorage.setItem('user', JSON.stringify(merged)); } catch {}
        return merged;
      });
      return me;
    } catch {
      // Non-blocking — AuthWrapper falls back to the pre-login user shape
      // (which has no features) and the Blocker simply isn't shown. The
      // server-side requireSkillDeliveryAllowed middleware still gates
      // the actual data routes, so there's no security hole.
      return null;
    }
  }, []);

  // Fire-and-forget /me refresh on first mount when a token exists.
  // Covers hard-refresh / open-in-new-tab for an already-authed user.
  useEffect(() => {
    if (isAuthenticated) {
      refreshMe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real login using backend API
  // V2.0: Extended to handle 2FA flow
  // Security phase: optional captchaToken (Cloudflare Turnstile), forwarded
  // to api.login → server verifies it when its counters demand.
  const login = async (email, password, captchaToken) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.login(email, password, captchaToken);

      if (response.success) {
        // V2.0: Check if 2FA is required
        if (response.requires2FA) {
          return {
            success: true,
            requires2FA: true,
            tempToken: response.tempToken,
            message: response.message
          };
        }

        const userData = response.data;
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', userData.token);
        // ShubbhYatra: fold /me features into the user so the
        // ShubbhYatraOnlyBlocker can render on first paint after login.
        // Not awaited — the login resolution shouldn't block on /me.
        refreshMe();
        return { success: true, data: userData };
      }
    } catch (err) {
      setError(err.message);

      // Preserve suspension data so Login component can handle it
      if (err.isSuspended || err.response?.data?.isSuspended || err.response?.data?.code === 'LOGIN_BLOCKED_SUSPENDED') {
        return {
          success: false,
          error: err.message,
          isSuspended: true,
          code: err.response?.data?.code || 'ACCOUNT_SUSPENDED',
          suspensionInfo: err.response?.data?.suspensionInfo || null,
          supportEmail: err.response?.data?.supportEmail || 'support@skilldelivery.co.in'
        };
      }

      // Preserve error response data for other error types
      return {
        success: false,
        error: err.message,
        code: err.response?.data?.code,
        attemptsRemaining: err.attemptsRemaining ?? err.response?.data?.attemptsRemaining,
        email: err.response?.data?.email,
        // Captcha phase — server signals when Turnstile should be shown on
        // the next submit (after 2 failed attempts on this email or IP).
        captcha_required: err.response?.data?.captcha_required === true
      };
    } finally {
      setLoading(false);
    }
  };

  // V2.0: Complete 2FA login
  const complete2FALogin = async (tempToken, twoFactorCode) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.verify2FA(tempToken, twoFactorCode);

      if (response.success) {
        const userData = response.data;
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', userData.token);
        // ShubbhYatra: same /me fold as the plain login path.
        refreshMe();
        return { success: true, data: userData };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Real register using backend API
  // NOTE: Does NOT auto-login - user must login after registration
  // V2.0: Extended to accept entity options (entityType, organizationName, subCategoryId, tcAccepted)
  const register = async (email, password, fullName, userType, vertical, v2Options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        email,
        password,
        fullName,
        userType,
        vertical,
        dateOfBirth: v2Options.dateOfBirth || null,
        // V2.0 Fields
        entityType: v2Options.entityType || 'individual',
        organizationName: v2Options.organizationName || '',
        subCategoryId: v2Options.subCategoryId || null,
        tcAccepted: v2Options.tcAccepted || false
      };
      // Captcha phase — forward the Turnstile token if the form collected one.
      if (v2Options.captchaToken) payload['cf-turnstile-response'] = v2Options.captchaToken;
      const response = await api.register(payload);
      
      if (response.success) {
        // DO NOT auto-login after registration
        // User will be redirected to login page
        return { success: true };
      } else {
        // Handle error response from API
        return { 
          success: false, 
          error: response.error || 'Registration failed',
          details: response.details 
        };
      }
    } catch (err) {
      // Handle different error formats
      const errorMessage = err.response?.data?.error || err.message || 'Registration failed';
      const errorDetails = err.response?.data?.details;
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        details: errorDetails
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      loading,
      error,
      login,
      complete2FALogin,  // V2.0
      register,
      logout,
      refreshMe
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
